'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

export const SIDEBAR_WIDTH_OPEN = 222;
export const SIDEBAR_WIDTH_COLLAPSED = 58;

interface SidebarContextType {
  /** Desktop expand (222px, labels) vs collapse (58px, icons). */
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  toggleSidebar: () => void;
  /**
   * Mobile drawer visibility. Kept separate from the desktop expand state so
   * the drawer can stay closed on first paint without a post-mount flip — the
   * source of the "opens for a sec then closes" flash on refresh.
   */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  /** Desktop sidebar width, derived from isOpen — used for the content margin. */
  sidebarWidth: number;
  isAnimating: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({
  children,
  // When true, the sidebar starts collapsed on desktop — e.g. an open chat
  // conversation wants more room.
  initialCollapsed = false,
}: {
  children: ReactNode;
  initialCollapsed?: boolean;
}) {
  const [isOpen, setIsOpenState] = useState(!initialCollapsed);
  const [mobileOpen, setMobileOpenState] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Enable the transition class for the duration of a state change so toggles
  // animate, while the initial render (and any resize) stays instant.
  const animate = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 350);
  }, []);

  // Desktop expand/collapse.
  const toggleSidebar = useCallback(() => {
    animate();
    setIsOpenState((prev) => !prev);
  }, [animate]);

  const setIsOpen = useCallback(
    (value: boolean) => {
      animate();
      setIsOpenState(value);
    },
    [animate],
  );

  // Mobile drawer open/close.
  const setMobileOpen = useCallback(
    (value: boolean) => {
      animate();
      setMobileOpenState(value);
    },
    [animate],
  );

  // 222px expanded, 58px collapsed.
  const sidebarWidth = isOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED;

  return (
    <SidebarContext.Provider
      value={{
        isOpen,
        setIsOpen,
        toggleSidebar,
        mobileOpen,
        setMobileOpen,
        sidebarWidth,
        isAnimating,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
